import { useState, useEffect, useCallback } from 'react';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();

// Convert URL-safe base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Backend uses Bearer-token auth via `require_auth` / `get_current_user`.
// Cookie-based `credentials: 'include'` silently 401s, which was the reason
// the Settings-page "Send Test Notification" button did nothing.
function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('gntl_token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Byte-equality for ArrayBuffer/Uint8Array — used to compare an existing
// browser subscription's applicationServerKey to the current VAPID key.
function bufEq(a, b) {
  const av = a instanceof ArrayBuffer ? new Uint8Array(a) : a;
  const bv = b instanceof ArrayBuffer ? new Uint8Array(b) : b;
  if (!av || !bv || av.byteLength !== bv.byteLength) return false;
  for (let i = 0; i < av.byteLength; i++) if (av[i] !== bv[i]) return false;
  return true;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        
        // Check if already subscribed
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        } catch (err) {
          console.error('Error checking subscription:', err);
        }
      }
      
      setLoading(false);
    };
    
    checkSupport();
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!isSupported) return null;
    
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (err) {
      console.error('Service Worker registration failed:', err);
      setError('Failed to register service worker');
      return null;
    }
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications not supported');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        setError('Notification permission denied');
        setLoading(false);
        return false;
      }
      
      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) {
        setLoading(false);
        return false;
      }
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      // Get VAPID public key from server
      const keyResponse = await fetch(`${API_URL}/api/push/vapid-public-key`);
      const { publicKey } = await keyResponse.json();
      
      if (!publicKey) {
        setError('VAPID key not configured');
        setLoading(false);
        return false;
      }

      const appKey = urlBase64ToUint8Array(publicKey);

      // If the browser already has a subscription tied to a different VAPID
      // key (e.g. after we rotated keys), throw it away so a fresh one is
      // created. Prevents "silent" delivery failures where the server can't
      // encrypt for a stale sub.
      let existing = await registration.pushManager.getSubscription();
      if (existing) {
        const existingKey = existing.options?.applicationServerKey;
        const sameKey = existingKey && bufEq(existingKey, appKey);
        if (!sameKey) {
          try { await existing.unsubscribe(); } catch { /* ignore */ }
          existing = null;
        }
      }

      // Subscribe to push
      const subscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appKey
      });
      
      // Send subscription to server
      const response = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ subscription: subscription.toJSON() })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save subscription');
      }

      // Fire the welcome push once, right after the subscription is saved.
      // Backend is idempotent via `welcome_push_sent`.
      fetch(`${API_URL}/api/push/welcome`, {
        method: 'POST',
        headers: authHeaders(),
      }).catch(() => {});

      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to subscribe');
      setLoading(false);
      return false;
    }
  }, [isSupported, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return false;
    
    setLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      // Notify server
      await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: authHeaders(),
      });
      
      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err.message || 'Failed to unsubscribe');
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  // Send a test notification
  const sendTestNotification = useCallback(async () => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/push/test`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setError(body.detail || `Test failed (${response.status}). Turn notifications off and back on to refresh your subscription.`);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Test notification error:', err);
      setError(err.message || 'Test notification failed');
      return false;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
}

export default usePushNotifications;
